import { useState, useRef, useEffect, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGES, CURRENCIES } from "../i18n";
import { Globe, ChevronDown } from "lucide-react";

const CurrencyContext = createContext({
  currency: CURRENCIES[0],
  setCurrency: () => {},
  convertPrice: (gbp) => gbp,
  formatPrice: (gbp) => `£${gbp}`,
});

export const useCurrency = () => useContext(CurrencyContext);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    const saved = localStorage.getItem("singleseen_currency");
    if (saved) {
      const found = CURRENCIES.find((c) => c.code === saved);
      if (found) return found;
    }
    return CURRENCIES[0];
  });

  const [liveRates, setLiveRates] = useState(null);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await fetch("https://api.exchangerate-api.com/v4/latest/GBP");
        const data = await res.json();
        if (data?.rates) setLiveRates(data.rates);
      } catch (e) {
        // Fall back to static rates
      }
    };
    fetchRates();
  }, []);

  const setCurrency = (c) => {
    setCurrencyState(c);
    localStorage.setItem("singleseen_currency", c.code);
  };

  const getRate = () => {
    if (liveRates && liveRates[currency.code]) return liveRates[currency.code];
    return currency.rate;
  };

  const convertPrice = (gbpAmount) => {
    const rate = getRate();
    const converted = gbpAmount * rate;
    if (rate >= 100) return Math.round(converted);
    return Math.round(converted * 100) / 100;
  };

  const formatPrice = (gbpAmount) => {
    const converted = convertPrice(gbpAmount);
    return `${currency.symbol}${converted.toLocaleString()}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convertPrice, formatPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
};

const LanguageCurrencySelector = () => {
  const { i18n, t } = useTranslation();
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const lang = LANGUAGES.find((l) => l.code === i18n.language);
    if (lang) {
      document.documentElement.dir = lang.dir;
      document.documentElement.lang = lang.code;
    }
  }, [i18n.language]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-rose-700 hover:bg-rose-50 transition-colors border border-rose-200/50"
        data-testid="lang-currency-btn"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLang.name}</span>
        <span className="text-xs text-rose-400">|</span>
        <span className="text-xs">{currency.code}</span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <div className="absolute right-0 sm:right-0 left-0 sm:left-auto top-full mt-2 w-72 bg-white rounded-xl shadow-xl shadow-rose-500/10 border border-rose-100 z-50 overflow-hidden" data-testid="lang-currency-dropdown">
          <div className="p-3 border-b border-rose-100">
            <p className="text-xs font-semibold text-rose-900 uppercase tracking-wide mb-2">{t('common.language')}</p>
            <div className="grid grid-cols-2 gap-1">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { i18n.changeLanguage(lang.code); }}
                  className={`text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                    i18n.language === lang.code
                      ? "bg-rose-100 text-rose-800 font-medium"
                      : "text-gray-600 hover:bg-rose-50"
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  {lang.flag} {lang.name}
                </button>
              ))}
            </div>
          </div>
          <div className="p-3">
            <p className="text-xs font-semibold text-rose-900 uppercase tracking-wide mb-2">{t('common.currency')}</p>
            <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
              {CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  onClick={() => { setCurrency(c); }}
                  className={`text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                    currency.code === c.code
                      ? "bg-rose-100 text-rose-800 font-medium"
                      : "text-gray-600 hover:bg-rose-50"
                  }`}
                  data-testid={`currency-${c.code}`}
                >
                  {c.symbol} {c.code}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageCurrencySelector;
